import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Alert from 'antd/lib/alert';
import { TRY_CREATE_ISSUE, tryCreateIssue } from '@/actions/triage';
import { RootState } from '@/reducers/reducers';
import { selectRelease, selectReleasesForAppId } from '@/selectors/selectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { IssueSourceRef } from '@/types';
import { InfoCircleOutlined } from '@ant-design/icons';
import { antGrey } from '@/components/styles/globalStyles';
import { AbsorbableSourcesSelect, ATTACH_SIGNALS_TOOLTIP } from './IssueCandidatesSelect';
import { buildSelectOptions, useBuildsForVersion } from './triageHelpers';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';

interface CreateIssueModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    appId: string;
    // Set at the release-level triage route: the release selector is then locked + prefilled to this release.
    lockedReleaseId?: string;
}

interface CreateIssueFormValues {
    title: string;
    description?: string;
    version?: string;
    build?: string;
}

export const CreateIssueModal: React.FC<CreateIssueModalProps> = ({ open, onClose, orgId, appId, lockedReleaseId }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<CreateIssueFormValues>();
    const [sources, setSources] = React.useState<IssueSourceRef[]>([]);

    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));
    const lockedRelease = useSelector((state: RootState) =>
        lockedReleaseId ? selectRelease(state, lockedReleaseId) : undefined,
    );
    const lockedVersion = lockedRelease?.version;

    // Build cascades from the selected release, so it's only enabled once a release/version is set.
    const selectedVersion = Form.useWatch('version', form) ?? lockedVersion;

    const { builds, buildsLoading } = useBuildsForVersion(orgId, appId, selectedVersion);

    const isSaving = useIsLoading(TRY_CREATE_ISSUE, appId);
    const saveError = useError(TRY_CREATE_ISSUE, appId);
    const saveSucceeded = useUpdated(TRY_CREATE_ISSUE, appId);

    // Close + reset once the create succeeds.
    React.useEffect(() => {
        if (saveSucceeded) {
            form.resetFields();
            setSources([]);
            onClose();
        }
    }, [saveSucceeded]);

    const onSubmit = (values: CreateIssueFormValues) => {
        dispatch(
            tryCreateIssue({
                orgId,
                appId,
                title: values.title.trim(),
                description: values.description?.trim() ?? '',
                version: values.version?.trim() || undefined,
                build: values.build?.trim() || undefined,
                sources: sources.length > 0 ? sources : undefined,
            }),
        );
    };

    return (
        <Modal
            title="Add a new issue"
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <PermissionedActionButton key="create" type="primary" loading={isSaving} onClick={() => form.submit()}>
                    Create issue
                </PermissionedActionButton>,
            ]}
            destroyOnClose
            width={560}
        >
            <Form form={form} layout="vertical" onFinish={onSubmit} requiredMark preserve={false}>
                {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
                <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
                    <Input placeholder="Title of the issue" autoFocus />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
                </Form.Item>
                <Form.Item name="version" label={lockedReleaseId ? 'Version' : 'Version'} initialValue={lockedVersion}>
                    <Select
                        placeholder="Select a version"
                        disabled={!!lockedReleaseId}
                        allowClear={!lockedReleaseId}
                        showSearch
                        optionFilterProp="label"
                        onChange={() => form.setFieldsValue({ build: undefined })}
                        options={releases.map((release) => ({
                            value: release.version,
                            label: release.displayableVersion || release.version,
                        }))}
                    />
                </Form.Item>
                <Form.Item name="build" label="Build">
                    <Select
                        placeholder={selectedVersion ? 'Select a build' : 'Select a version first'}
                        disabled={!selectedVersion}
                        loading={buildsLoading}
                        allowClear
                        showSearch
                        optionFilterProp="searchLabel"
                        notFoundContent={buildsLoading ? 'Loading…' : 'No builds found'}
                        options={buildSelectOptions(builds)}
                    />
                </Form.Item>
                <Form.Item
                    label="Attach existing signals"
                    tooltip={{
                        title: ATTACH_SIGNALS_TOOLTIP,
                        icon: <InfoCircleOutlined style={{ color: antGrey.grey7, cursor: 'default' }} />,
                    }}
                >
                    <AbsorbableSourcesSelect
                        orgId={orgId}
                        appId={appId}
                        open={open}
                        value={sources}
                        onChange={setSources}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
